// Beheer › Plannen › de pagina van één plan (Airtable 19, het eindontwerp van
// "Plan instellen en rekenen", editie 1 tot 4).
//
// Vier blokken onder elkaar: het plan (wie vult in staat vast na de start), de
// mensen (AM en BP wisselen kan na de start; het werk van de vorige persoon blijft bewaard),
// delen (de leeslink en de vaste versies) en onderaan testkeuzes wissen en de
// laatste wijzigingen. Het scherm rekent niets: alles komt uit
// GET /api/beheer/plannen/{p}/{j} (contract plan-pagina-v1).

import { ArrowLeft, Mail } from "lucide-react";
import { type ReactNode, useState } from "react";

import type { PlanMens, PlanPagina as Pagina } from "../../contracts";
import { ApiError, api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import { LeeslinkBlok } from "../../nieuw/plan/Leeslink";
import {
  Foutmelding,
  Invoer,
  Knop,
  Notitie,
  type Persoon,
  PersoonRegel,
  PersoonVeld,
  PlatformStijl,
} from "../../stijl";
import { DemoOpnieuw } from "./DemoOpnieuw";
import "./planpagina.css";

const INVULLEN_WOORD = { am_bp: "AM en BP", am: "Alleen AM", bp: "Alleen BP" } as const;
const ROL_LABEL = { am: "Accountmanagement", bp: "Opleidingsmanager (BP)" } as const;

const DAG = new Intl.DateTimeFormat("nl-NL", { day: "numeric", month: "long", year: "numeric" });
const datum = (iso: string) => DAG.format(new Date(iso));
function tijd(iso: string): string {
  return new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** "1.3 · 6 van 12", of een zin zolang er niets gebeurd is. */
function standTekst(p: Pagina): string {
  if (!p.gestart) return "Nog niet gestart";
  if (!p.stand.paragraaf) return "Nog niet begonnen";
  return `${p.stand.paragraaf} · ${p.stand.klaar} van ${p.stand.van}`;
}

function alsPersoon(m: PlanMens, sub: string): Persoon {
  return { id: m.gebruiker, naam: m.naam, email: m.email, sub: m.isTest ? `test · ${sub}` : sub };
}

export function PlanPagina({
  propositie,
  jaar,
  onTerug,
}: {
  propositie: string;
  jaar: number;
  onTerug: () => void;
}) {
  const res = useResource(() => api.planPagina(propositie, jaar), [propositie, jaar]);
  const [eigen, setEigen] = useState<Pagina | null>(null);
  const p = eigen ?? res.data;
  const herlaad = () => {
    setEigen(null);
    res.reload();
  };

  return (
    <PlatformStijl>
      <div className="pp">
        <div>
          <Knop variant="link" icoon={ArrowLeft} onClick={onTerug}>
            Alle plannen
          </Knop>
        </div>
        {res.error && !p ? (
          <Foutmelding>{res.error}</Foutmelding>
        ) : !p ? (
          <p className="s-veldblok__uitleg">Laden…</p>
        ) : (
          <Inhoud p={p} zet={setEigen} herlaad={herlaad} />
        )}
      </div>
    </PlatformStijl>
  );
}

function Blok({ titel, children, uitleg }: { titel: string; children: ReactNode; uitleg?: ReactNode }) {
  return (
    <section className="pp-blok" aria-label={titel}>
      <h3 className="pp-blok__kop">{titel}</h3>
      {uitleg && <p className="s-veldblok__uitleg">{uitleg}</p>}
      {children}
    </section>
  );
}

function Inhoud({ p, zet, herlaad }: { p: Pagina; zet: (p: Pagina) => void; herlaad: () => void }) {
  const plan = { propositie: p.propositie, jaar: p.jaar };
  return (
    <>
      <header className="pp-kop">
        <h2>
          Plan {p.naam} {p.jaar}
        </h2>
        <span className="s-veldblok__uitleg">
          {p.gestart && p.gestartOp ? `Gestart op ${datum(p.gestartOp)}` : "Nog niet gestart"}
        </span>
      </header>

      <Blok titel="Plan">
        <dl className="pp-feiten">
          <dt>Wie vult in</dt>
          <dd>
            {p.invullen ? INVULLEN_WOORD[p.invullen] : "Nog niet ingesteld"}
            {p.gestart && <span className="pp-slot"> · staat vast tot het plan af is</span>}
          </dd>
          <dt>Stand</dt>
          <dd>{standTekst(p)}</dd>
          <dt>Invloed</dt>
          <dd>{p.invloed.tekst.replace(/^Invloed:\s*/, "")}</dd>
        </dl>
        {!p.gestart && <Notitie>Start het plan via "Instellen" in de lijst van plannen.</Notitie>}
        {p.gestart && <Terugzetten p={p} zet={zet} />}
      </Blok>

      <Blok titel="Mensen">
        <Mensen p={p} zet={zet} herlaad={herlaad} />
      </Blok>

      <Blok
        titel="Delen"
        uitleg="De leeslink toont altijd de jongste vaste versie. Een AM of BP maakt een versie met &quot;Deel deze versie&quot;."
      >
        <LeeslinkBlok plan={plan} begin={p.leeslink} onVerander={herlaad} />
        <Versies p={p} herlaad={herlaad} />
      </Blok>

      <Blok titel="Wijzigingen">
        {p.wijzigingen.length ? (
          <ul className="pp-log">
            {p.wijzigingen.map((w, i) => (
              <li key={`${w.wanneer}-${i}`}>
                <span className="pp-log__tijd">{tijd(w.wanneer)}</span> · <b>{w.naam}</b> · {w.wat}
              </li>
            ))}
          </ul>
        ) : (
          <p className="s-veldblok__uitleg">Nog geen wijzigingen.</p>
        )}
        <TestkeuzesWissen p={p} herlaad={herlaad} />
        {p.isDemo && <DemoOpnieuw propositie={p.propositie} jaar={p.jaar} naam={p.naam} klaar={herlaad} />}
      </Blok>
    </>
  );
}

function Mensen({ p, zet, herlaad }: { p: Pagina; zet: (p: Pagina) => void; herlaad: () => void }) {
  const [kiest, setKiest] = useState<"am" | "bp" | null>(null);
  const [email, setEmail] = useState("");
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);

  const melding = (e: unknown, anders: string) => setFout(e instanceof ApiError ? e.message || e.reason : anders);

  const wissel = async (rol: "am" | "bp", gebruiker: string | null) => {
    setBezig(true);
    setFout(null);
    try {
      zet(await api.wisselPlan(p.propositie, p.jaar, { rol, gebruiker }));
      setKiest(null);
    } catch (e) {
      melding(e, "Wisselen is niet gelukt.");
    } finally {
      setBezig(false);
    }
  };

  const voegToe = async () => {
    const adres = email.trim();
    if (!adres) return;
    setFout(null);
    try {
      await api.voegPlanMeelezerToe(p.propositie, p.jaar, adres);
      setEmail("");
      herlaad();
    } catch (e) {
      melding(e, "Toevoegen is niet gelukt.");
    }
  };

  const trekIn = async (gebruiker: string) => {
    setFout(null);
    try {
      await api.trekPlanMeelezerIn(p.propositie, p.jaar, gebruiker);
      herlaad();
    } catch (e) {
      melding(e, "Intrekken is niet gelukt.");
    }
  };

  const stoel = (rol: "am" | "bp") => {
    const sub = `${ROL_LABEL[rol]} · ${p.naam}`;
    const nu = p[rol];
    const vultIn = p.invullen === "am_bp" || p.invullen === rol;
    const label = rol === "am" ? "AM" : "BP";
    const lijst = p.kandidaten[rol].filter((k) => k.gebruiker !== nu?.gebruiker).map((k) => alsPersoon(k, sub));
    return (
      <div className="s-stoel" data-stoel={rol}>
        <span className="s-lbl">
          {label}
          {!vultIn && " · vult niet in"}
        </span>
        {kiest === rol || !nu ? (
          p.gestart ? (
            <>
              <PersoonVeld
                label={`${label} kiezen`}
                personen={lijst}
                onKies={(x) => void wissel(rol, x.id)}
                leeg={`Er is niemand anders met de rol ${ROL_LABEL[rol].toLowerCase()} bij ${p.naam}. Nodig iemand uit bij Gebruikers.`}
              />
              {kiest === rol && (
                <Knop variant="link" onClick={() => setKiest(null)}>
                  Annuleren
                </Knop>
              )}
            </>
          ) : (
            <p className="s-veldblok__uitleg">Nog niemand: kies bij "Instellen".</p>
          )
        ) : (
          <PersoonRegel
            persoon={alsPersoon(nu, nu.email)}
            rechts={
              p.gestart ? (
                <span className="pp-acties">
                  <Knop className="s-persoon__actie" disabled={bezig} aria-label={`${label}: wissel ${nu.naam}`} onClick={() => setKiest(rol)}>
                    Wissel
                  </Knop>
                  {!vultIn && (
                    <Knop className="s-persoon__actie" disabled={bezig} aria-label={`${label}: van dit plan halen`} onClick={() => void wissel(rol, null)}>
                      Van dit plan halen
                    </Knop>
                  )}
                </span>
              ) : undefined
            }
          />
        )}
      </div>
    );
  };

  return (
    <div className="s-formulier">
      <div className="s-stoelen">
        {stoel("am")}
        {stoel("bp")}
      </div>
      {p.gestart && <Notitie>
          Bij een wissel blijft het werk van de vorige persoon bewaard. De stand telt het werk van wie nu op
          het plan staat. De wissel staat in de wijzigingen.
        </Notitie>}

      <div className="s-veldblok">
        <span className="s-lbl">Meelezen (alleen lezen)</span>
        {p.meelezers.length ? (
          <ul className="s-mensenlijst" aria-label="Meelezers">
            {p.meelezers.map((m) => (
              <li key={m.gebruiker}>
                <PersoonRegel
                  persoon={alsPersoon(m, `${m.email} · sinds ${datum(m.sinds)}`)}
                  rechts={
                    <Knop className="s-persoon__actie" aria-label={`Intrekken: ${m.naam}`} onClick={() => void trekIn(m.gebruiker)}>
                      Intrekken
                    </Knop>
                  }
                />
              </li>
            ))}
          </ul>
        ) : (
          <span className="s-veldblok__uitleg">Nog niemand.</span>
        )}
        <div className="s-invoerrij">
          <Invoer
            icoon={Mail}
            type="email"
            value={email}
            placeholder="naam@summacollege.nl"
            aria-label="E-mailadres van een meelezer"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void voegToe()}
          />
          <Knop variant="zacht" disabled={!email.trim()} onClick={() => void voegToe()}>
            Toevoegen
          </Knop>
        </div>
        <span className="s-veldblok__uitleg">Een meelezer heeft een Summa-account en logt in om te lezen.</span>
      </div>
      {fout && <Foutmelding>{fout}</Foutmelding>}
    </div>
  );
}

function Versies({ p, herlaad }: { p: Pagina; herlaad: () => void }) {
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const maak = async () => {
    setBezig(true);
    setFout(null);
    try {
      await api.maakPlanVersie(p.propositie, p.jaar);
      herlaad();
    } catch (e) {
      setFout(e instanceof ApiError ? e.message || e.reason : "Een versie maken is niet gelukt.");
    } finally {
      setBezig(false);
    }
  };
  return (
    <div className="s-veldblok">
      <span className="s-lbl">Vaste versies</span>
      {p.versies.length ? (
        <ul className="pp-log" aria-label="Vaste versies">
          {p.versies.map((v, i) => (
            <li key={v.id}>
              <b>{v.naam}</b> · {datum(v.gemaakt)}
              {v.door ? ` · door ${v.door}` : ""}
              {i === 0 && p.leeslink ? <span className="pp-slot"> · in de leeslink</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        <span className="s-veldblok__uitleg">Nog geen vaste versie.</span>
      )}
      {p.gestart && (
        <div>
          <Knop disabled={bezig} onClick={() => void maak()}>
            {bezig ? "Bezig…" : `Maak ${p.volgendeNaam}`}
          </Knop>
        </div>
      )}
      {fout && <Foutmelding>{fout}</Foutmelding>}
    </div>
  );
}

/** Terug naar "niet gestart" (owner 04-11): daarna kiest beheer opnieuw wie invult,
 *  bijvoorbeeld alleen de AM. De keuzes blijven staan. */
function Terugzetten({ p, zet }: { p: Pagina; zet: (p: Pagina) => void }) {
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const terug = async () => {
    const vraag =
      `Het plan van ${p.naam} (${p.jaar}) terugzetten naar "niet gestart"?\n\n` +
      "Daarna kies je opnieuw wie invult. De gekozen AM en BP gaan van het plan; hun keuzes, de meelezers en de versies blijven staan.";
    if (!window.confirm(vraag)) return;
    setBezig(true);
    setFout(null);
    try {
      zet(await api.planTerugzetten(p.propositie, p.jaar));
    } catch (e) {
      setFout(e instanceof ApiError ? e.message || e.reason : "Terugzetten is niet gelukt.");
    } finally {
      setBezig(false);
    }
  };
  return (
    <div className="pp-wis">
      <Knop disabled={bezig} onClick={() => void terug()}>
        {bezig ? "Bezig met terugzetten…" : "Plan terugzetten naar niet gestart"}
      </Knop>
      {fout && <Foutmelding>{fout}</Foutmelding>}
    </div>
  );
}

function TestkeuzesWissen({ p, herlaad }: { p: Pagina; herlaad: () => void }) {
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState<{ fout: boolean; tekst: string } | null>(null);
  const wis = async () => {
    const vraag =
      `Testkeuzes wissen in het plan van ${p.naam} (${p.jaar})?\n\n` +
      "Alleen de keuzes van testaccounts en van “beheer als AM/BP” gaan weg. Echte keuzes van een AM of BP blijven staan.";
    if (!window.confirm(vraag)) return;
    setBezig(true);
    setMelding(null);
    try {
      const r = await api.testkeuzesWissen(p.propositie, p.jaar);
      setMelding({ fout: false, tekst: r.gewist === 1 ? "1 testkeuze gewist." : `${r.gewist} testkeuzes gewist.` });
      herlaad();
    } catch (e) {
      setMelding({ fout: true, tekst: e instanceof ApiError ? e.message || e.reason : "Wissen is niet gelukt." });
    } finally {
      setBezig(false);
    }
  };
  return (
    <div className="pp-wis">
      <Knop disabled={bezig} onClick={() => void wis()}>
        {bezig ? "Bezig met wissen…" : "Testkeuzes wissen"}
      </Knop>
      {melding &&
        (melding.fout ? <Foutmelding>{melding.tekst}</Foutmelding> : <span className="s-veldblok__uitleg">{melding.tekst}</span>)}
    </div>
  );
}
